import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';

import {
    CalculatorStepPanel,
    QuestionCompact,
    LocationSelector,
    Sizes,
    createOptionMessageResolver,
    useForm,
    LegacyCityPropType,
    CondensedTheme,
    useCalculator,
    IconChooser,
    Colours,
} from '@ratehub/base-ui';
import {
    MORTGAGE_PLACEHOLDERS,
    SCENARIO_TYPES,
} from '@ratehub/mtg-common';

import DOWNPAYMENT_INSURABILITY_OPTIONS from '../definitions/DownPaymentInsurabilityOptions';
import PaymentCalculatorScenarioGrid from './PaymentCalculatorScenarioGrid';


function PaymentCalculatorScenarioStep({
    stepNumber,
    scenarioCount,
    initialAskingPrice,
    initialProvinceCode,
    initialCity,
    preferredProvider,
    isProductReferralDisabled,
    ...otherProps
}) {
    const form = useForm();
    const intl = useIntl();
    const calculatorState = useCalculator();

    const { scenarioType } = form.values;
    const document = calculatorState.document;

    const {
        askingPrice,
        location,
        scenarios,
        isFirstTimeBuyer,
    } = getSavedFormValues(toJS(document?.journey?.calculators));

    const isStartHereVisible = scenarioCount > (scenarioType === SCENARIO_TYPES.PURCHASE ? 1 : 2);

    return (
        <CondensedTheme>
            <Container
                stepNumber={stepNumber}
                includeInnerPadding={false}
                isVisible
                {...otherProps}
            >
                <div className="top-container rh-px-1">
                    <div className="inline-container rh-pb-0_5">
                        <If condition={isStartHereVisible}>
                            <div className="start-here rh-text-m weight-medium rh-my-0">
                                <FormattedMessage
                                    id="calculators.PaymentCalculatorInputs.startHere"
                                    defaultMessage="Start here"
                                />
                                <IconChooser
                                    className="icon-arrow"
                                    iconKey="arrow-right"
                                    stroke={Colours.BLACKBERRY}
                                    size="L"
                                    strokeWidth="2px"
                                    outlineWidth="0"
                                />
                            </div>
                        </If>

                        <div className="inline-question">
                            <div className="question-container">

                                <Choose>
                                    <When condition={scenarioType === SCENARIO_TYPES.PURCHASE}>
                                        <QuestionCompact
                                            className="input"
                                            field={{
                                                name: 'askingPrice',
                                                type: 'integer',
                                                value: askingPrice ?? initialAskingPrice,
                                            }}
                                            type="currency"
                                            labelMessage={(
                                                <FormattedMessage
                                                    id="mtg-calculator.PaymentCalculatorInputs.askingPriceLabel"
                                                    defaultMessage="Asking price"
                                                />
                                            )}
                                            placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                                        />
                                    </When>
                                    <Otherwise>
                                        <QuestionCompact
                                            className="input"
                                            field={{
                                                name: 'mortgageAmount',
                                                type: 'integer',
                                                value: initialAskingPrice,
                                            }}
                                            type="currency"
                                            labelMessage={(
                                                <FormattedMessage
                                                    id="mtg-calculator.PaymentCalculatorInputs.mortgageAmountLabel"
                                                    defaultMessage="Mortgage amount"
                                                />
                                            )}
                                            placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                                        />
                                    </Otherwise>
                                </Choose>
                            </div>

                            <div className="question-container">
                                <LocationSelector
                                    name="location"
                                    id="payment-location"
                                    label={(
                                        <FormattedMessage
                                            id="mtg-calculator.PaymentCalculatorInputs.locationLabel"
                                            defaultMessage="Location"
                                        />
                                    )}
                                    placeholder={(
                                        <FormattedMessage
                                            id="mtg-calculator.PaymentCalculatorInputs.locationPlaceholder"
                                            defaultMessage="Enter city"
                                        />
                                    )}
                                    className="input"

                                    initialProvince={initialProvinceCode}
                                    value={location ?? initialCity}

                                    isRequired
                                />
                            </div>

                            <If condition={scenarioType === SCENARIO_TYPES.RENEWAL}>
                                <div className="question-container">
                                    <QuestionCompact
                                        className="input"
                                        field={{
                                            name: 'downPaymentPercentage',
                                            type: 'choice',
                                            value: DOWNPAYMENT_INSURABILITY_OPTIONS.LESS,
                                            options: [
                                                DOWNPAYMENT_INSURABILITY_OPTIONS.MORE,
                                                DOWNPAYMENT_INSURABILITY_OPTIONS.LESS,
                                            ],
                                        }}
                                        type="dropdown"
                                        labelMessage={(
                                            <FormattedMessage
                                                id="mtg-calculator.PaymentCalculatorInputs.originalDownPaymentLabel"
                                                defaultMessage="Original down payment %"
                                            />
                                        )}
                                        renderOptionLabel={createOptionMessageResolver(
                                            intl,
                                            Object.values(DOWNPAYMENT_OPTIONS),
                                            'mtg-calculator.PaymentCalculatorInputs.downPayment-option-',
                                        )}
                                    />
                                </div>
                            </If>
                        </div>
                    </div>
                    <hr className="rh-horizontal-rule rh-mt-1 rh-mb-0_5 rh-mx-auto rh-width-100p" />
                </div>

                <PaymentCalculatorScenarioGrid
                    scenarioCount={scenarioCount}
                    values={{
                        scenarios,
                        isFirstTimeBuyer,
                    }}
                    preferredProvider={preferredProvider}
                    isProductReferralDisabled={isProductReferralDisabled}
                />
            </Container>
        </CondensedTheme>
    );
}

function getSavedFormValues(savedCalculatorData) {
    const property = savedCalculatorData?.property;
    const cityName = property?.address?.city;
    const provinceCode = property?.address?.province;

    return {
        askingPrice: property?.homeValue,
        location: (cityName && provinceCode) && {
            name: cityName,
            province: { code: provinceCode },
        },
        scenarios: savedCalculatorData?.payment?.mortgages?.map(scenario => ({
            rateType: scenario?.type,
            rateTerm: scenario?.term,
            downPayment: scenario?.downPayment,
            paymentFrequency: scenario?.paymentFrequencyType,
        })),
        isFirstTimeBuyer: savedCalculatorData?.firstTimeHomeBuyer,
    };
}

PaymentCalculatorScenarioStep.propTypes = {
    stepNumber: PropTypes.number.isRequired,
    scenarioCount: PropTypes.number.isRequired,
    initialAskingPrice: PropTypes.number,
    initialProvinceCode: PropTypes.string,
    initialCity: LegacyCityPropType,
    preferredProvider: PropTypes.string,
    isProductReferralDisabled: PropTypes.bool,
};

PaymentCalculatorScenarioStep.defaultProps = {
    initialAskingPrice: undefined,
    initialProvinceCode: undefined,
    initialCity: undefined,
    preferredProvider: undefined,
    isProductReferralDisabled: undefined,
};


const DOWNPAYMENT_OPTIONS = defineMessages({
    moreThan20: {
        id: 'mtg-calculator.PaymentCalculatorInputs.downPayment-option-more',
        defaultMessage: '20% or more',
    },
    lessThan20: {
        id: 'mtg-calculator.PaymentCalculatorInputs.downPayment-option-less',
        defaultMessage: 'Less than 20%',
    },
});

const FLEX_GAP = Sizes.SPACING.ONE;

const Container = styled(CalculatorStepPanel)`
    overflow: hidden; // scenarioCount defaults to 4 on initial render, so on smaller screens we need to hide the overflow
    padding: ${Sizes.SPACING.TWO} 0;

    > .top-container {
        > .inline-container {
            display: flex;
            flex-wrap: nowrap;
            gap: ${FLEX_GAP};

            > .start-here {
                /* The largest it should be is 1/5 of the row minus the flex gap */
                flex-basis: calc(20% - ${FLEX_GAP});

                display: flex;
                align-items: center;
                white-space: nowrap;

                > .icon-arrow {
                    flex-shrink: 0;
                }
            }

            > .inline-question {
                flex-grow: 1;

                display: flex;
                flex-wrap: wrap;
                gap: 0.625rem;

                > .question-container {
                    flex: 1 0;
                    min-width: 10.5rem;

                    .input {
                        margin: 0;
                    }
                }
            }

            .input {
                margin-bottom: ${Sizes.SPACING.TWO};
            }
        }
    }
`;

export default observer(PaymentCalculatorScenarioStep);
